/**
 * Breadcrumb Component
 * Navigation path with separators
 * WCAG 2.1 AA compliant
 */

import React from 'react';
import { Icon } from '@/components/atoms/Icon/Icon';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbProps {
  /**
   * Breadcrumb items
   */
  items: BreadcrumbItem[];
  /**
   * Custom separator icon
   */
  separator?: 'chevron' | 'slash';
  /**
   * Custom className
   */
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  separator = 'chevron',
  className = '',
}) => {
  const separatorIcon = separator === 'chevron' ? 'chevron-right' : undefined;

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex items-center space-x-2">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isClickable = !isLast && (item.href || item.onClick);

          return (
            <li key={index} className="flex items-center">
              {isClickable ? (
                <a
                  href={item.href}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  }}
                  className="text-primary-600 hover:text-primary-700 hover:underline transition-colors duration-fast"
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </a>
              ) : (
                <span
                  className={isLast ? 'text-neutral-900 font-medium' : 'text-neutral-600'}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}

              {!isLast && (
                <span className="mx-2 text-neutral-400" aria-hidden="true">
                  {separatorIcon ? (
                    <Icon name={separatorIcon} size={16} ariaHidden />
                  ) : (
                    '/'
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
